import {
  PauseCircle,
  PlayCircleOutlineOutlined,
  Refresh,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import { usePageContext } from "@ryde/redux";
import {
  liveMapPollingUiAtom,
  selectedLiveMapMapRidesSF,
  sortAscendingNumbers,
  TOOLTIP_DELAY_BUTTONS,
  useIsMobile,
} from "@ryde/shared";

import { LiveMapContext } from "../../context";
import { InvestigationTriggerButton } from "../../investigation";
import { LiveMapDiagnosticsControl } from "./live-map.diagnostics.control";
import { LiveMapMapDisplayedSummary } from "./live-map.map-displayed-summary";
import { LiveMapPollStatusPill } from "./live-map.poll-status-pill";
import { LiveMapRefreshIntervalControl } from "./live-map.refresh-interval.control";
import {
  LiveMapPollingControlsGroupStyled,
  LiveMapTopControlsStyled,
} from "./live-map.styled";
import { useLiveMapMapPollTooltip } from "./use-live-map-map-poll-tooltip";
import { useLiveMapToolbarStats } from "./use-live-map-toolbar-stats";

export const LiveMapTopControlsMap: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { mapRidesCount } = useLiveMapToolbarStats();
  const mapPollTooltip = useLiveMapMapPollTooltip();
  const {
    initGridPolling,
    stopGridPolling,
    initMapPolling,
    stopMapPolling,
    refreshMapSelection,
  } = useContext(LiveMapContext);
  const {
    mapPollingRideIds,
    lastSynchroMapDate,
    isMapPollFetchInFlight,
    isGridPollingRunning,
  } = useRecoilValue(liveMapPollingUiAtom);
  const mapRides = useRecoilValue(selectedLiveMapMapRidesSF(entity));
  const mapRideIdsSorted = useMemo(() => {
    const ids = mapRides
      .map(r => r.id)
      .filter((id): id is number => typeof id === "number" && id > 0);

    return sortAscendingNumbers(new Set(ids));
  }, [mapRides]);

  const isMapPollingActive = Boolean(mapPollingRideIds?.length);
  const useGridPauseOnMobile = isMobile && mapRideIdsSorted.length < 1;
  const isPauseActive = useGridPauseOnMobile
    ? Boolean(isGridPollingRunning)
    : isMapPollingActive;

  const onRefreshMapClick = useEvent(() => {
    refreshMapSelection?.();
  });

  const onPauseResumeClick = useEvent(() => {
    if (useGridPauseOnMobile) {
      if (isGridPollingRunning) {
        stopGridPolling?.();
      } else {
        initGridPolling?.();
      }
      return;
    }

    if (isMapPollingActive) {
      stopMapPolling?.();
    } else {
      void initMapPolling?.(mapRideIdsSorted);
    }
  });

  const pauseResumeTooltip = useMemo(() => {
    if (useGridPauseOnMobile) {
      return isGridPollingRunning
        ? t("rideMap.PAUSE_GRID_UPDATE")
        : t("rideMap.RESUME_GRID_UPDATE");
    }

    return isMapPollingActive
      ? t("rideMap.PAUSE_MAP_UPDATE")
      : t("rideMap.RESUME_MAP_UPDATE");
  }, [isGridPollingRunning, isMapPollingActive, t, useGridPauseOnMobile]);

  if (mapRides.length < 1 && !isMobile) {
    return null;
  }

  return (
    <LiveMapTopControlsStyled>
      <div className="status_synchro map">
        <FramerTooltip
          content={t("rideMap.FETCH_MAP_STATE")}
          mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
        >
          <IconButton
            aria-label={t("rideMap.FETCH_MAP_STATE")}
            size="small"
            color="info"
            onClick={onRefreshMapClick}
          >
            <Refresh />
          </IconButton>
        </FramerTooltip>
        <LiveMapPollingControlsGroupStyled>
          <LiveMapRefreshIntervalControl
            variant="map"
            hideSecInput={isMobile}
          />
          <FramerTooltip
            content={pauseResumeTooltip}
            mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
          >
            <IconButton size="small" onClick={onPauseResumeClick}>
              {isPauseActive && <PauseCircle />}
              {!isPauseActive && <PlayCircleOutlineOutlined />}
            </IconButton>
          </FramerTooltip>
        </LiveMapPollingControlsGroupStyled>
        <InvestigationTriggerButton />
        {isMobile && <LiveMapDiagnosticsControl compactLabel />}
        {!isMobile && (
          <LiveMapPollStatusPill
            tooltip={mapPollTooltip}
            isInFlight={Boolean(isMapPollFetchInFlight)}
            isPollingActive={isMapPollingActive}
            hasSyncedAtLeastOnce={Boolean(lastSynchroMapDate)}
          >
            <LiveMapMapDisplayedSummary ridesCount={mapRidesCount} />
          </LiveMapPollStatusPill>
        )}
      </div>
    </LiveMapTopControlsStyled>
  );
};
